import "./SpecificMailLog.css"

import React, { useContext, useEffect, useState } from "react"
import { Menu, Dropdown, Table, Input, Button, Space, Typography, Modal, Tooltip } from "antd"
import { DownOutlined, SettingOutlined, PushpinOutlined, PushpinFilled } from "@ant-design/icons"
import { DatePicker } from "antd"
import { SearchOutlined } from "@ant-design/icons"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"

// get global state context
import { GlobalStoreContext } from "../../Store"
import apiConstants from "../../api"
import constants from "../../constants"

dayjs.extend(utc)
dayjs.extend(timezone)

const { RangePicker } = DatePicker

const MailLog = () => {
	// get mutations and state variables from global state
	const { setMenuToolBarTitle, getMailLogRows, failMessage } = useContext(GlobalStoreContext)

	const [selectedClient, changeClient] = useState(apiConstants.SSE_CLIENTS[0])
	const [rows, setRows] = useState([])
	const [dates, setDates] = useState([dayjs().tz("Asia/Kolkata").subtract(1, "days"), dayjs().tz("Asia/Kolkata")])
	const [searchObj, setSearchObj] = useState({})
	const [loading, toggleLoading] = useState(true)
	const [currentPageNo, setPageNo] = useState(1)
	const [currentPageSize, setPageSize] = useState(7)
	const [currentTotal, setCurrentTotal] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	
	// Column management state
	const [columnOrder, setColumnOrder] = useState(() => {
		const saved = localStorage.getItem('criticalMailLogColumnOrder')
		return saved ? JSON.parse(saved) : constants.MAIL_LOG_COLUMNS.map((col) => col.dataIndex)
	})
	const [frozenColumns, setFrozenColumns] = useState(() => {
		const saved = localStorage.getItem('criticalMailLogFrozenColumns')
		return saved ? JSON.parse(saved) : ['uuid']
	})
	const [columnManageModalVisible, setColumnManageModalVisible] = useState(false)

	// when component mounted
	useEffect(() => {
		// set the menu tool bar title text
		setMenuToolBarTitle("Search Critical Mail Log")
	}, [setMenuToolBarTitle])
	
	// Save column order and frozen columns to localStorage
	useEffect(() => {
		localStorage.setItem('criticalMailLogColumnOrder', JSON.stringify(columnOrder))
	}, [columnOrder])
	
	useEffect(() => {
		localStorage.setItem('criticalMailLogFrozenColumns', JSON.stringify(frozenColumns))
	}, [frozenColumns])

	const handleMenuItemClick = function (e) {
		changeClient(apiConstants.SSE_CLIENTS.find((c) => c.name === e.key.split("_")[0]))
	}

	// watcher
	useEffect(() => {
		toggleLoading(true)
		let mounted = true
		getMailLogRows({
			client: selectedClient,
			filter: { ...searchObj, stockistIds: selectedClient.criticalStockists },
			dates,
			pagination: { currentPageNo, currentPageSize },
		})
			.then((response) => {
				if (mounted) {
					setRows(
						response.rows.map((e) => {
							e.hide = e.hide !== undefined ? String(e.hide) : "false"
							e.mapped = e.mapped !== undefined ? String(e.mapped) : "false"
							e.reintroduced = e.reintroduced !== undefined ? String(e.reintroduced) : "false"
							e.date = dayjs(e.date).tz("Asia/Kolkata").format("DD MMMM YYYY HH:mm:ss")
							return e
						})
					)
					setCurrentTotal(response.total)
					toggleLoading(false)
				}
			})
			.catch((e) => {
				if (mounted) {
					failMessage(e.message)
					toggleLoading(false)
				}
			})

		return () => (mounted = false)
		// eslint-disable-next-line
	}, [dates, selectedClient, searchObj, currentPageNo, currentPageSize])

	const menu = (
		<Menu>
			{apiConstants.SSE_CLIENTS.map((e, idx) => {
				return (
					<Menu.Item key={e.name + "_" + idx} onClick={handleMenuItemClick}>
						{e.name}
					</Menu.Item>
				)
			})}
		</Menu>
	)

	const getColumnSearchProps = (dataIndex) => {
		if (dataIndex === "date") return null
		return {
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
				<div style={{ padding: 8 }}>
					<Input
						placeholder={`Search ${dataIndex}`}
						value={selectedKeys[0]}
						onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
						onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
						style={{
							width: 188,
							marginBottom: 8,
							display: "block",
						}}
					/>
					<Space>
						<Button
							type="primary"
							onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
							icon={<SearchOutlined />}
							size="small"
							style={{ width: 90 }}
						>
							Search
						</Button>
						<Button onClick={() => handleReset(clearFilters, dataIndex)} size="small" style={{ width: 90 }}>
							Reset
						</Button>
					</Space>
				</div>
			),
			filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />,
			render: (text) => text,
		}
	}
	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm()
		if (selectedKeys[0]) setSearchObj({ ...searchObj, [dataIndex]: selectedKeys[0] })
	}

	const handleReset = (clearFilters, dataIndex) => {
		clearFilters()
		delete searchObj[dataIndex]
		setSearchObj({ ...searchObj })
	}
	
	// Column management functions
	const handleDragEnd = (result) => {
		if (!result.destination) return
		
		const items = Array.from(columnOrder)
		const [reorderedItem] = items.splice(result.source.index, 1)
		items.splice(result.destination.index, 0, reorderedItem)
		
		setColumnOrder(items)
	}
	
	const toggleFreezeColumn = (columnKey) => {
		setFrozenColumns((prev) => {
			if (prev.includes(columnKey)) {
				return prev.filter((key) => key !== columnKey)
			} else {
				return [...prev, columnKey]
			}
		})
	}
	
	const resetColumnSettings = () => {
		const defaultOrder = constants.MAIL_LOG_COLUMNS.map((col) => col.dataIndex)
		setColumnOrder(defaultOrder)
		setFrozenColumns(['uuid'])
		localStorage.removeItem('criticalMailLogColumnOrder')
		localStorage.removeItem('criticalMailLogFrozenColumns')
		failMessage('Column settings reset to default')
	}
	
	const getColumnByDataIndex = (dataIndex) => {
		return constants.MAIL_LOG_COLUMNS.find((col) => col.dataIndex === dataIndex)
	}

	const columns = columnOrder
		.map((dataIndex) => {
			const col = getColumnByDataIndex(dataIndex)
			if (!col) return null
			return {
				...col,
				...getColumnSearchProps(col.title),
			}
		})
		.filter(Boolean)
	
	// Apply frozen state to columns
	frozenColumns.forEach((frozenKey) => {
		const colIndex = columns.findIndex((col) => col.key === frozenKey || col.dataIndex === frozenKey)
		if (colIndex !== -1) {
			columns[colIndex].fixed = "left"
		}
	})

	const showModal = () => {
		setIsModalVisible(true)
	}

	const handleOk = () => {
		setIsModalVisible(false)
	}

	const handleCancel = () => {
		setIsModalVisible(false)
	}

	return (
		<div className="critical-mail-log-page">
			<div
				style={{
					display: "flex",
					paddingBottom: "15px",
					justifyContent: "space-between",
				}}
			>
				<Dropdown overlay={menu}>
					{/* eslint-disable-next-line */}
					<a className="ant-dropdown-link" onClick={(e) => e.preventDefault()}>
						Change Client <DownOutlined />
					</a>
				</Dropdown>

				<Button onClick={showModal} size="small">
					Show Critical Stockist List
				</Button>

				<Typography.Title type="danger" strong>
					{selectedClient.name} Critical Stockists Log
				</Typography.Title>
				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button 
						icon={<SettingOutlined />} 
						onClick={() => setColumnManageModalVisible(true)}
						type="default"
					>
						Manage Columns
					</Button>
					<RangePicker
						value={dates}
						onChange={(changedDates) => {
							setDates(changedDates)
						}}
					/>
				</div>
			</div>
			<Space />
			<Modal title="Basic Modal" visible={isModalVisible} onOk={handleOk} onCancel={handleCancel}>
				{selectedClient.criticalStockists.map(function (object, i) {
					return <p key={i}>{object}</p>
				})}
			</Modal>
			
			<Modal
				title="Manage Columns"
				visible={columnManageModalVisible}
				onCancel={() => setColumnManageModalVisible(false)}
				footer={[
					<Button key="reset" onClick={resetColumnSettings}>
						Reset to Default
					</Button>,
					<Button key="close" type="primary" onClick={() => setColumnManageModalVisible(false)}>
						Close
					</Button>,
				]}
				width={600}
			>
				<div style={{ marginBottom: "20px" }}>
					<Typography.Text strong>Drag to reorder columns. Click the pin icon to freeze/unfreeze a column.</Typography.Text>
				</div>
				<DragDropContext onDragEnd={handleDragEnd}>
					<Droppable droppableId="columns">
						{(provided) => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								{columnOrder.map((dataIndex, index) => {
									const col = getColumnByDataIndex(dataIndex)
									if (!col) return null
									const isFrozen = frozenColumns.includes(dataIndex)
									
									return (
										<Draggable key={dataIndex} draggableId={dataIndex} index={index}>
											{(provided, snapshot) => (
												<div
													ref={provided.innerRef}
													{...provided.draggableProps}
													{...provided.dragHandleProps}
													className={`column-item ${snapshot.isDragging ? 'dragging' : ''}`}
												>
													<div className="column-item-content">
														<span className="drag-handle">☰</span>
														<span className="column-name">{col.title}</span>
														<Tooltip title={isFrozen ? "Click to unfreeze" : "Click to freeze"}>
															{isFrozen ? (
																<PushpinFilled 
																	className="freeze-icon frozen" 
																	onClick={() => toggleFreezeColumn(dataIndex)}
																/>
															) : (
																<PushpinOutlined 
																	className="freeze-icon" 
																	onClick={() => toggleFreezeColumn(dataIndex)}
																/>
															)}
														</Tooltip>
													</div>
												</div>
											)}
										</Draggable>
									)
								})}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</Modal>
			<Table
				bordered={true}
				columns={columns}
				dataSource={rows}
				pagination={{
					position: ["bottomCenter"],
					onChange: (page) => setPageNo(page),
					onShowSizeChange: (current, size) => setPageSize(size),
					current: currentPageNo,
					pageSize: currentPageSize,
					total: currentTotal,
				}}
				loading={loading}
				scroll={{ x: 1500 }}
			/>
		</div>
	)
}
export default MailLog
